// M4 E1 + E2 — Workspace switcher in the header.
//
// Dropdown that lists the caller's workspaces and lets them switch. On switch,
// setCurrentWorkspaceId fires an in-page event; other components (evidence
// list) refetch off that. First render triggers ensureActiveWorkspace so a
// brand-new sign-in without a persisted id lands on the personal workspace.
//
// E2 adds the "+ Create workspace" entry which opens CreateWorkspaceDialog.

import { useEffect, useRef, useState } from "react";
import {
  Workspace,
  listWorkspaces,
  getCurrentWorkspaceId,
  setCurrentWorkspaceId,
  subscribeWorkspaceChanges,
  ensureActiveWorkspace,
} from "../lib/workspace";
import { CreateWorkspaceDialog } from "./CreateWorkspaceDialog";

export function WorkspaceSwitcher() {
  const [workspaces, setWorkspaces] = useState<Workspace[] | null>(null);
  const [currentId, setCurrentId] = useState<string | null>(getCurrentWorkspaceId());
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Load workspaces + bootstrap active id on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await ensureActiveWorkspace();
        const { workspaces } = await listWorkspaces();
        if (cancelled) return;
        setWorkspaces(workspaces);
        setCurrentId(getCurrentWorkspaceId());
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load workspaces");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Track workspace changes (from other tabs, from Create dialog, etc.)
  useEffect(() => {
    return subscribeWorkspaceChanges((id) => setCurrentId(id));
  }, []);

  // Close the dropdown on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const current = workspaces?.find((w) => w.workspaceId === currentId) ?? null;

  function pick(w: Workspace) {
    setCurrentWorkspaceId(w.workspaceId);
    setOpen(false);
    // Callers listening via subscribeWorkspaceChanges refetch. As a fallback
    // for anything that only reads workspace on mount, reload the current view.
    window.location.reload();
  }

  function openCreate() {
    setOpen(false);
    setCreateOpen(true);
  }

  if (error) {
    return <span className="text-xs text-ink-soft" title={error}>Workspaces unavailable</span>;
  }
  if (!workspaces) {
    return <span className="text-xs text-ink-soft">Loading workspaces...</span>;
  }
  if (workspaces.length === 0) {
    // No workspaces at all: user needs to create one. Show a direct CTA
    // instead of an empty dropdown. This is a very rare state (backend
    // auto-provisions a personal workspace on first sign-in) but worth handling.
    return (
      <>
        <button
          type="button"
          onClick={openCreate}
          className="px-3 py-1.5 rounded-md border border-border bg-white hover:bg-neutral-50 text-sm text-ink"
        >
          + Create workspace
        </button>
        <CreateWorkspaceDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
        />
      </>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border bg-white hover:bg-neutral-50 text-sm"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="w-2 h-2 rounded-full bg-aigentico-navy" aria-hidden />
        <span className="font-medium text-ink truncate max-w-[140px] sm:max-w-[180px]">
          {current ? current.name : "Select workspace"}
        </span>
        <span className="text-ink-soft" aria-hidden>▾</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-72 max-w-[calc(100vw-1.5rem)] rounded-md border border-border bg-white shadow-lg z-20"
        >
          <ul className="py-1 max-h-80 overflow-auto">
            {workspaces.map((w) => {
              const active = w.workspaceId === currentId;
              return (
                <li key={w.workspaceId}>
                  <button
                    type="button"
                    onClick={() => pick(w)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-neutral-50 ${active ? "bg-neutral-50" : ""}`}
                    role="menuitem"
                  >
                    <span className="flex flex-col min-w-0">
                      <span className="font-medium text-ink truncate">{w.name}</span>
                      <span className="text-xs text-ink-soft">
                        {w.kind === "personal" ? "Personal" : "Team"} · {w.role}
                      </span>
                    </span>
                    {active && <span className="text-aigentico-navy text-xs" aria-label="Current">●</span>}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="border-t border-border">
            <button
              type="button"
              onClick={openCreate}
              className="w-full text-left px-3 py-2 text-sm text-ink hover:bg-neutral-50"
              role="menuitem"
            >
              + Create workspace
            </button>
          </div>
        </div>
      )}

      <CreateWorkspaceDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </div>
  );
}
